import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import {
  CONVERSATION_END,
  CONVERSATION_START,
  CONVERSATION_UPDATE,
  ConversationStart,
  type ConversationMessage,
} from '@rsos/hermes-js/src/typings/conversation';
import {
  ConversationErrorRecord,
  ConversationLoadingRecord,
  ConversationTransfer,
  conversationStateValues,
  conversationTCAPI,
  isTCAPIConversation,
} from './conversations.typings';
import { tcapiAPI } from './tcapiAPI';

/**
 * A Redux slice that contains and bundles all possible conversations and their data together
 * Only stores 1 main conversation at a time
 */
const conversationsSlice = createSlice({
  name: 'conversations',
  initialState: {} as Record<string, conversationStateValues>,
  reducers: {
    setConversationUnread: (
      state,
      action: PayloadAction<{
        isUnread: boolean;
        callerID: string | null | undefined;
      }>,
    ) => {
      const { isUnread, callerID } = action.payload;

      if (!callerID) return;

      if (state[callerID]) {
        state[callerID] = {
          ...state[callerID],
          isUnread: isUnread,
        };
      }
    },
    setIsConversationCreated: (
      state,
      action: PayloadAction<string | undefined | null>,
    ) => {
      const callerID = action.payload;

      if (!callerID) return;

      const conversation = state[callerID];

      if (conversation && conversation.data) {
        if (conversation.source === SOURCE_TCAPI) {
          conversation.data.acceptStatus == 'accepted';
          conversation.data.status = 'active';
        }
      }
    },
    setIsConversationEnded: (
      state,
      action: PayloadAction<string | undefined | null>,
    ) => {
      const callerID = action.payload;

      if (!callerID) return;

      const conversation = state[callerID];

      if (conversation && conversation.data) {
        if (conversation.source === SOURCE_TCAPI) {
          conversation.data.status = 'inactive';
        }
      }
    },
    addConversation: (
      state,
      action: PayloadAction<ConversationStart['body']>,
    ) => {
      const body = action.payload;
      const callerID = body?.caller_id;

      if (!callerID) return;

      state[callerID] = {
        data: body,
        source: body.source,
        isUnread: false,
        error: {} as ConversationErrorRecord,
        loading: {} as ConversationLoadingRecord,
        recentMessageType: CONVERSATION_START,
      } as conversationStateValues;
    },
    updateConversation: (
      state,
      action: PayloadAction<{
        body: ConversationMessage['body'];
        messageType: typeof CONVERSATION_UPDATE | typeof CONVERSATION_END;
      }>,
    ) => {
      const body = action.payload.body;
      const callerID = body?.caller_id;

      if (callerID && state[callerID]) {
        state[callerID] = {
          ...state[callerID],
          recentMessageType: action.payload.messageType,
          data: {
            ...state[callerID].data,
            ...body,
          },
        } as conversationStateValues;
      }
    },
    clearConversation: (state, action: PayloadAction<string>) => {
      const callerID = action.payload;
      delete state[callerID];
    },
    transferConversation: (
      state,
      action: PayloadAction<{
        callerID: string;
        transfer: ConversationTransfer;
      }>,
    ) => {
      const { callerID, transfer } = action.payload;

      const conversation = state[callerID];
      if (!conversation) {
        // eslint-disable-next-line no-console -- logging for sentry
        console.error(
          `transferConversation: no conversation exists for callerID ${callerID}.`,
        );
        return;
      }

      if (!isTCAPIConversation(conversation)) {
        // eslint-disable-next-line no-console -- only TCAPI supports transfers today, error to notify if this changes
        console.error(
          `transferConversation: callerID ${callerID} is non-TCAPI conversation (source: '${conversation.source}').`,
        );
      }

      conversation.transfer = transfer;
    },
    receiveTransferredConversation: (
      state,
      action: PayloadAction<{
        callerID: string;
        transfer: ConversationTransfer;
      }>,
    ) => {
      const { callerID, transfer } = action.payload;

      if (state[callerID]) {
        // eslint-disable-next-line no-console -- logging for sentry
        console.error(
          `receiveTransferredConversation: conversation already exists for callerID ${callerID}, expected fresh entry.`,
        );
        state[callerID].transfer = transfer;
        return;
      }

      // Intentionally creating in a pending state with `data: null` (see
      // `conversationTCAPIPending`). The conversation metadata will be hydrated
      // by getTCAPIConversationMetadata after the receiving agency accepts the
      // transfer and the queue attempts to use the metadata with a RTK query
      // hook.
      state[callerID] = {
        source: SOURCE_TCAPI,
        data: null,
        isUnread: false,
        error: {
          getConversationMetadata: null,
          createConversation: null,
          endConversation: null,
        },
        loading: {
          getConversationMetadata: false,
          createConversation: false,
          endConversation: false,
        },
        recentMessageType: CONVERSATION_START,
        transfer,
      } satisfies conversationTCAPI;
    },
    setCreateConversationLoading: (
      state,
      action: PayloadAction<{ callerID: string; isLoading: boolean }>,
    ) => {
      const { callerID, isLoading } = action.payload;

      if (state[callerID]) {
        state[callerID] = {
          ...state[callerID],
          loading: {
            ...state[callerID].loading,
            createConversation: isLoading,
          },
        };
      }
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchFulfilled,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;
          const existingConversation = state[callerID];

          // Replace stale inactive TCAPI session with new active/pending session data
          // This prevents old ended sessions from blocking new sessions on the same number
          if (
            existingConversation?.source === SOURCE_TCAPI &&
            existingConversation?.data?.status === 'inactive' &&
            action.payload?.status !== 'inactive'
          ) {
            delete state[callerID];
          }

          // Keep inactive sessions in state to allow UI to hide buttons and show "Closed Chat" status
          state[callerID] = {
            ...state[callerID],
            source: SOURCE_TCAPI,
            data: action.payload,
            loading: {
              ...state?.[callerID]?.loading,
              getConversationMetadata: false,
            },
          };
        },
      )
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchPending,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;

          state[callerID] = {
            ...state?.[callerID],
            source: SOURCE_TCAPI,
            data: null,
            error: {
              ...state?.[callerID]?.error,
              getConversationMetadata: null,
            },
            loading: {
              ...state?.[callerID]?.loading,
              getConversationMetadata: true,
            },
            isUnread: false,
          };
        },
      )
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchRejected,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;

          // NOTE: We intentionally delete the conversation entry here rather than following
          // the typical pattern of preserving the entry with error state. This is because:
          // 1. A 404 response indicates the TCAPI session legitimately doesn't exist (not a temporary error)
          // 2. Stale TCAPI state can persist from previous sessions (e.g., Redux persist or page state)
          // 3. We need conversationSource to be falsy so CallChats.tsx renders the correct component
          //    (TF911ChatBox for TF911 sessions, not ConversationsChatBox)
          // 4. Keeping source="tcapi" with data=null would cause incorrect routing to TCAPI APIs
          //    for non-TCAPI sessions
          // By deleting the entry, we allow the UI to fall back to the appropriate chat component
          // based on the actual session type (TF911, RCS, etc.)
          delete state[callerID];
        },
      )
      .addMatcher(
        /**
         * This reducer will run on all state mutations, to preserve the invariant
         * of: conversation.status==inactive && conversation.transfer === undefined
         * Otherwise we can end up showing transfer banners based on transfer
         * state when a conversation is inactive (e.g. if it was in a pending
         * state just before ending it).
         */
        () => true,
        state => {
          Object.keys(state).forEach(callerID => {
            const conversation = state[callerID];

            if (
              conversation.source === SOURCE_TCAPI &&
              conversation.data?.status === 'inactive' &&
              conversation.transfer
            ) {
              delete conversation.transfer;
            }
          });
        },
      );
  },
});

export function selectConversation(callerId: string) {
  return (state: {
    conversations: Record<string, conversationStateValues>;
  }) => {
    return state.conversations?.[callerId];
  };
}

export function selectConversationId(callerId: string) {
  return createSelector(
    [selectConversation(callerId)],
    (conversation?: conversationStateValues) => {
      if (!conversation) {
        return undefined;
      }
      switch (conversation.source) {
        case SOURCE_ADMIN_SMS:
          return conversation.data?.conversation_id;
        case SOURCE_TCAPI:
          return conversation.data?.conversationId;
      }
    },
  );
}

export function selectConversationUnread(callerId: string) {
  return createSelector(
    [selectConversation(callerId)],
    conversation => conversation?.isUnread,
  );
}

const persistConfig = {
  key: 'conversation',
  storage,
};

const persistedReducer = persistReducer(
  persistConfig,
  conversationsSlice.reducer,
);

export const {
  setConversationUnread,
  setIsConversationCreated,
  setIsConversationEnded,
  addConversation,
  clearConversation,
  transferConversation,
  receiveTransferredConversation,
  setCreateConversationLoading,
  updateConversation,
} = conversationsSlice.actions;

export default persistedReducer;
